/**
 * La consulta: lo que el cliente junto, y el mensaje que se va a mandar.
 *
 * LO QUE ESTO NO ES
 * -----------------
 * No es un checkout. No cobra, no reserva stock y no toca la caja. Eso esta
 * dicho en la pantalla con todas las letras, porque un boton que dice
 * "Consultar" al lado de un total se parece demasiado a un "Comprar".
 *
 * EL MENSAJE SE VE ANTES DE ENVIARSE
 * ----------------------------------
 * El boton abre WhatsApp con el texto ya escrito, o sea que escribe en nombre de
 * la persona. Mostrarlo entero antes no es un lujo: es la unica forma de que
 * nadie mande algo que no leyo.
 */
import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { formatear } from "@tora/shared";

import { cn } from "@/lib/utils";

import { useFotosDeModelo } from "./consultas";
import { Foto, IconoWhatsapp, Precio } from "./piezas";
import {
  cuantosProductos,
  fotoDeColor,
  linkWhatsapp,
  mensajeDeConsulta,
  numeroWhatsapp,
  total,
  type Consulta,
  type ProductoPublico,
} from "./logica";

export interface PanelConsultaProps {
  abierto: boolean;
  onAbiertoChange: (v: boolean) => void;
  /** Lo que sigue publicado, con el precio de hoy (`consultaAlDia`). */
  consulta: Consulta;
  /** Lo que se agrego y ya no se publica: se dice, y no va al mensaje. */
  noDisponibles?: Consulta;
  /** Para dibujar la miniatura de cada linea. */
  porId: Map<number, ProductoPublico>;
  whatsapp: string | null;
  onAgregar: (id: number) => void;
  onQuitar: (id: number) => void;
  onSacar: (id: number) => void;
}

export function PanelConsulta({
  abierto,
  onAbiertoChange,
  consulta,
  noDisponibles = [],
  porId,
  whatsapp,
  onAgregar,
  onQuitar,
  onSacar,
}: PanelConsultaProps): JSX.Element {
  const { data: fotos } = useFotosDeModelo();
  const [entrega, setEntrega] = useState<string | null>(null);
  const mensaje = mensajeDeConsulta(consulta, new Date(), entrega);
  const link = linkWhatsapp(numeroWhatsapp(whatsapp), mensaje);

  // Al sacar lo ultimo, el panel se cierra: abierto quedaba «Total $ 0,00» con
  // «Enviar por WhatsApp» activo, que mandaba un «Hola!» sin nada.
  const vacio = consulta.length === 0 && noDisponibles.length === 0;
  useEffect(() => {
    if (abierto && vacio) onAbiertoChange(false);
  }, [abierto, vacio, onAbiertoChange]);

  return (
    <DialogPrimitive.Root open={abierto} onOpenChange={onAbiertoChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-[16px] bg-white text-[#0A0A0A]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            // En la PC es un panel al costado, como el carrito de cualquier
            // tienda: la pagina sigue a la vista y se ve que se agrego.
            "md:inset-y-0 md:left-auto md:right-0 md:max-h-none md:w-[440px] md:rounded-none md:rounded-l-[16px]",
            "md:data-[state=closed]:slide-out-to-right md:data-[state=open]:slide-in-from-right",
            "md:data-[state=closed]:slide-out-to-top-0 md:data-[state=open]:slide-in-from-top-0",
          )}
        >
          <div className="flex items-start justify-between gap-3 px-5 pb-1 pt-5">
            <div>
              <DialogPrimitive.Title className="text-[22px] font-[700] tracking-[-0.03em]">
                Tu consulta
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-1 text-[13px] leading-normal text-[#5C5C5C]">
                Esto no es una compra. Nos llega por WhatsApp y te confirmamos disponibilidad y
                forma de pago.
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              aria-label="Cerrar"
              className="-mr-1 flex size-9 shrink-0 items-center justify-center rounded-md text-[#5C5C5C]"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                aria-hidden="true"
                className="size-5"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </DialogPrimitive.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5">
            <ul>
              {/* Lo que se despublico desde que se agrego va al final, con la
                  misma fila: se dice que no esta, no suma y no va al mensaje,
                  pero no desaparece sin explicacion. */}
              {[
                ...consulta.map((l) => ({ l, disponible: true })),
                ...noDisponibles.map((l) => ({ l, disponible: false })),
              ].map(({ l, disponible }) => {
                const p = disponible ? porId.get(l.id) : undefined;
                return (
                  <li key={l.id} className="flex gap-3 border-b border-[#ECECEC] py-4">
                    {/* En el color elegido: el 18 Pro Black salia con la foto Burgundy. */}
                    {p ? (
                      <Foto
                        producto={p}
                        enVezDe={fotoDeColor(fotos ?? [], p.nombre, l.color ?? null)}
                        chico
                        className="size-[62px] shrink-0"
                      />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2.5">
                        <span className="text-sm font-semibold tracking-[-0.015em]">
                          {l.nombre}
                        </span>
                        {disponible ? (
                          <Precio
                            pesos={formatear(l.precio * l.cantidad, "ARS")}
                            className="whitespace-nowrap text-[15px]"
                          />
                        ) : null}
                      </div>
                      <p className="mt-0.5 text-xs text-[#6E6E6E]">
                        {disponible
                          ? [l.detalle, l.estado].filter(Boolean).join(" · ")
                          : "No va en el mensaje."}
                      </p>

                      <div className="mt-2 flex items-center justify-between gap-2.5">
                        {l.esUnico || !disponible ? (
                          <span className="text-xs text-[#6E6E6E]">
                            {disponible ? "Unidad única" : "Ya no está disponible"}
                          </span>
                        ) : (
                          <div className="flex items-center gap-0.5 rounded-md border border-[#E0E0E0] p-0.5">
                            <button
                              type="button"
                              onClick={() => onQuitar(l.id)}
                              aria-label={`Sacar uno de ${l.nombre}`}
                              className="flex size-10 items-center justify-center"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                aria-hidden="true"
                                className="size-3.5"
                              >
                                <path d="M5 12h14" />
                              </svg>
                            </button>
                            <span className="min-w-4 text-center text-sm font-[700] tabular-nums">
                              {l.cantidad}
                            </span>
                            <button
                              type="button"
                              onClick={() => onAgregar(l.id)}
                              aria-label={`Agregar uno más de ${l.nombre}`}
                              className="flex size-10 items-center justify-center"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                aria-hidden="true"
                                className="size-3.5"
                              >
                                <path d="M12 5v14M5 12h14" />
                              </svg>
                            </button>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => onSacar(l.id)}
                          aria-label={`Sacar ${l.nombre} de la consulta`}
                          // Alto de dedo: la palabra sola media 33 x 16.
                          className="-mr-2 inline-flex min-h-10 items-center px-2 text-xs text-[#6E6E6E] underline"
                        >
                          Sacar
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="mt-4 flex items-baseline justify-between">
              <span className="text-[15px] font-semibold">Total</span>
              <Precio pesos={formatear(total(consulta), "ARS")} className="text-[25px]" />
            </div>
            <p className="mt-1 text-[11px] text-[#6E6E6E]">
              Precios de hoy. No reservamos stock hasta confirmarlo por WhatsApp.
            </p>

            {/* Parte de pago: el pie lo promete y el mensaje no tenia como
                decirlo. Se dice aca, con el equipo, y viaja en el mensaje. */}
            <div className="mt-5 rounded-lg border border-[#E0E0E0] px-4 py-3">
              <label className="flex min-h-10 cursor-pointer items-center gap-3 text-[13px] font-semibold">
                <input
                  type="checkbox"
                  checked={entrega !== null}
                  onChange={(e) => setEntrega(e.target.checked ? "" : null)}
                  className="size-5 shrink-0 accent-[#0A0A0A]"
                />
                Quiero entregar mi equipo como parte de pago
              </label>
              {entrega !== null ? (
                <input
                  value={entrega}
                  onChange={(e) => setEntrega(e.target.value)}
                  placeholder="¿Cuál? Ej.: iPhone 11 128 GB, batería 80 %"
                  aria-label="El equipo que entregás"
                  className="mt-2 h-11 w-full rounded-md border border-[#E0E0E0] px-3 text-base outline-none placeholder:text-[#8A8A8A] focus:border-[#0A0A0A] focus-visible:ring-0 focus-visible:ring-offset-0 md:text-sm"
                />
              ) : null}
            </div>

            {/* El texto exacto que se va a enviar, a la vista. */}
            <div className="mt-5">
              <h3 className="text-[13px] font-[700]">El mensaje que se envía</h3>
              <pre className="mt-2 whitespace-pre-wrap rounded-lg bg-[#F7F7F7] px-4 py-3.5 font-mono text-[11px] leading-relaxed text-[#3A3A3A]">
                {mensaje}
              </pre>
            </div>
          </div>

          {/* `env(safe-area-inset-bottom)`: en el iPhone la barrita de inicio se
              come el borde de abajo y el boton queda cortado. */}
          <div className="border-t border-[#ECECEC] px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4">
            {link ? (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="flex h-[54px] items-center justify-center gap-2.5 rounded-lg bg-[#0A0A0A] text-[15px] font-[700] text-white"
              >
                <IconoWhatsapp className="size-[19px]" />
                Enviar por WhatsApp
              </a>
            ) : (
              <p className="rounded-lg bg-[#F7F7F7] px-4 py-3 text-center text-[13px] text-[#5C5C5C]">
                El local todavía no cargó su WhatsApp.
              </p>
            )}
            <p className="mt-2.5 text-center text-[11px] text-[#6E6E6E]">
              Se abre tu WhatsApp con el mensaje escrito
            </p>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * La barra de abajo. Aparece recien cuando hay algo adentro.
 *
 * Mostrarla vacia seria un control que no hace nada ocupando la unica franja de
 * la pantalla que siempre esta al alcance del pulgar.
 */
export function BarraConsulta({
  consulta,
  vigentes = consulta,
  onAbrir,
}: {
  consulta: Consulta;
  /** Lo que sigue publicado, con el precio de hoy: es lo que suma. */
  vigentes?: Consulta;
  onAbrir: () => void;
}): JSX.Element | null {
  // Se muestra mientras haya algo guardado, aunque ya no este publicado: es la
  // unica forma de abrir el panel y sacarlo.
  if (cuantosProductos(consulta) === 0) return null;
  const cuantos = cuantosProductos(vigentes);

  return (
    // El `env(safe-area-inset-bottom)` es por el iPhone: sin el, el boton queda
    // debajo de la barrita de inicio y se toca la mitad.
    // En la PC no cruza la pantalla: es un boton flotante abajo a la derecha,
    // donde se busca el carrito, y sin el degrade que tapa la grilla.
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 pb-[max(1rem,env(safe-area-inset-bottom))] pt-16 md:left-auto md:right-6 md:pb-6 md:pt-0">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-white via-white/95 to-transparent md:hidden" />
      <button
        type="button"
        onClick={onAbrir}
        className="pointer-events-auto relative mx-3.5 flex h-[60px] w-[calc(100%-1.75rem)] items-center gap-3 rounded-[10px] bg-[#0A0A0A] pl-[18px] pr-2 text-left text-white md:mx-0 md:w-[380px] md:shadow-[0_25px_50px_-12px_rgb(0_0_0/0.25)] md:ring-1 md:ring-white/25"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] text-[#A8A8A8]">
            {cuantos === 0
              ? "Nada disponible"
              : cuantos === 1
                ? "1 producto"
                : `${cuantos} productos`}
          </span>
          <Precio pesos={formatear(total(vigentes), "ARS")} className="block text-[17px]" />
        </span>
        <span className="flex h-11 items-center gap-1.5 rounded-[7px] bg-white px-4 text-sm font-[700] text-[#0A0A0A]">
          <IconoWhatsapp className="size-4" />
          Consultar
        </span>
      </button>
    </div>
  );
}
